import * as R from "ramda";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../../../../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../../../../core/redux/IAppState";

import { ACTION_TYPE } from "../../../../core/redux/actionType";
import {
  addError,
  addLoading,
  getLoading,
  removeLoadingAndError,
} from "../../../../core/redux/api/module";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../../../../core/redux/api/circuitBreaker/module";
import { API_SEASON_REPRINT_COUNT } from "../../../../core/redux/api/endpoints";
import { GuestSeasonReprintCount } from "./guestSeasonReprintCountResponse";
import { updateGuest } from "../../../module";


export const KEY_ACTION = ACTION_TYPE.GET_SEASON_REPRINT_COUNT_REQUEST;
export const ACTIONS = [
  ACTION_TYPE.GET_SEASON_REPRINT_COUNT_REQUEST,
  ACTION_TYPE.GET_SEASON_REPRINT_COUNT_RESPONSE,
  ACTION_TYPE.GET_SEASON_REPRINT_COUNT_ERROR,
];

/**
 * Makes an action that can be dispatched to get the total number of pass reprints for a season for each guest in a
 * comma separated list of guests
 */
export const getSeasonReprintCountAction = (reprintStartDate: Date, masterGuestIds: string) =>
  makeApiAction({
    method: "GET",
    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: API_SEASON_REPRINT_COUNT(encodeURIComponent(reprintStartDate!.toISOString()), masterGuestIds),
    types: ACTIONS.map((action) => ({
      type: action,
      meta: { masterGuestIds },
    })) as RsaaTypes,
    // Bail if already loading results for that query
    bailout: (state: IAppState) => hasTrippedBreaker(state, KEY_ACTION) || Boolean(getLoading(state, masterGuestIds, KEY_ACTION)),
  });

const reducer: ApiReducer<GuestSeasonReprintCount[]> = (state, action, error, isLoading, payload) => {
  const masterGuestIds = action?.meta?.masterGuestIds ?? 'default';

  if (isLoading) {
    return addLoading(state, masterGuestIds, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    /* Clear all passReprintCounts */
    const errorState = state.guests.reduce(
    (running, guest) =>
      updateGuest(
        running,
        guest,                        
        R.assocPath(['passReprintCount'], undefined) 
      ),
      withBreaker
    );

    return addError(errorState, masterGuestIds, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, masterGuestIds, KEY_ACTION);

  // Ensure payload is an array
  const results: GuestSeasonReprintCount[] = Array.isArray(payload) ? payload : [];

  // Iterate over each guest and update passReprintCount
  const nextState = results.reduce(
    (running, guest) =>
      updateGuest(
        running,
        String(guest.masterGuestId),                        
        R.assocPath(['passReprintCount'], guest.reprintCount) 
      ),
    noLoadingOrError
  );

  return nextState;
};

export const getSeasonReprintCountReducer = makeApiReducer<GuestSeasonReprintCount[]>(ACTIONS, KEY_ACTION, reducer);
